import { ComponentProps, ElementType } from 'react';
import { DynamicDefaultProps } from '@synergy/utils';
import { FormattedMessage } from 'react-intl';
import { PoolDynamicDefaultPropsComponent } from '@synergy/abstract';
import { Theme, Typography, TypographyProps } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { PoolFormattedMessage } from '../formatted-message/pool-formatted-message';

// Section headings follow PaloFormSection: small uppercase label on a hairline,
// so a legacy screen and a migrated one read the same.
const sectionHeading = (theme: Theme) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.disabled,
    fontSize: '10.5px',
    fontWeight: 640,
    letterSpacing: '.08em',
    textTransform: 'uppercase' as const,
    lineHeight: '14px',
    position: 'relative' as const,
});

const styles = (theme: Theme) =>
    createStyles({
        sectionTitle: {
            ...sectionHeading(theme),
            paddingBottom: '5px',
            marginBottom: '12px',
        },
        sectionTitleWithSwitch: {
            ...sectionHeading(theme),
            marginBottom: '12px',
            justifyContent: 'center',
        },
        sectionTitleNew: {
            ...sectionHeading(theme),
            paddingBottom: '5px',
        },
    });

interface PoolTitleBaseProps extends WithStyles<typeof styles> {
    styleType?: keyof ReturnType<typeof styles>;
    id?: string;
    values?: ComponentProps<typeof FormattedMessage>['values'];
    component?: ElementType;
}

export type PoolTitleProps = PoolTitleBaseProps & TypographyProps;

/**
 *  @deprecated Use components-next Typography
 */
export const PoolTypography = withStyles(styles)(
    class PoolTypographyBase extends PoolDynamicDefaultPropsComponent<PoolTitleProps> {
        protected override dynamicDefaultProps: DynamicDefaultProps<PoolTitleProps> = {
            sectionTitle: {
                variant: 'subtitle1',
            },
            sectionTitleNew: {
                variant: 'subtitle1',
            },
        };

        private static getDisplayClass(props: PoolTitleProps): string {
            return props.styleType ? props.classes[props.styleType] : '';
        }

        private static getClassNames(props: PoolTitleProps) {
            return `${this.getDisplayClass(props)} ${props.className || ''}`;
        }

        public override render() {
            const { defaultProps } = this.state;
            const { styleType: _styleType, classes: _classes, id, values, ...baseTitleProps } = this.props;
            const props = { ...defaultProps, ...this.props };

            return (
                <Typography {...defaultProps} {...baseTitleProps} className={PoolTypographyBase.getClassNames(props)}>
                    {id ? <PoolFormattedMessage id={id} values={values} /> : props.children}
                </Typography>
            );
        }
    }
);
